import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { exportHmrcPayouts, exportHmrcAnnualSummary } from "@/lib/hmrc-export.functions";
import { currentTaxYear, taxYearRange } from "@/lib/hmrc-shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReserveHeader } from "@/components/ReserveHeader";


export const Route = createFileRoute("/_authenticated/admin/hmrc-export")({
  head: () => ({ meta: [{ title: "HMRC Export — Admin" }] }),
  component: HmrcExportPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">{error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const pad = (n: number) => String(n).padStart(2, "0");
const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function presetRange(kind: "this_month" | "last_month" | "tax_year"): { from: string; to: string } {
  const now = new Date();
  if (kind === "this_month") {
    return { from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), to: fmt(now) };
  }
  if (kind === "last_month") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: fmt(first), to: fmt(last) };
  }
  // UK tax year: 6 April to 5 April
  const y = now.getMonth() > 3 || (now.getMonth() === 3 && now.getDate() >= 6) ? now.getFullYear() : now.getFullYear() - 1;
  return { from: `${y}-04-06`, to: fmt(now) };
}

function HmrcExportPage() {
  const today = fmt(new Date());
  const monthStart = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [taxYear, setTaxYear] = useState(currentTaxYear());

  const downloadTyped = (content: string, name: string, mime = "text/csv") => {
    const blob = new Blob([content], { type: `${mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const annual = useMutation({
    mutationFn: exportHmrcAnnualSummary,
    onSuccess: (res) => {
      if ("error" in res) { toast.error(res.error); return; }
      const ext = res.format === "json" ? "json" : "csv";
      const mime = res.format === "json" ? "application/json" : "text/csv";
      downloadTyped(res.content, `hmrc-dpr-annual-summary-${res.label.replace("/", "-")}.${ext}`, mime);
      toast.success(`${res.workers} worker${res.workers === 1 ? "" : "s"} exported as ${ext.toUpperCase()}.`);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Export failed."),
  });

  const mut = useMutation({
    mutationFn: exportHmrcPayouts,
    onSuccess: (res) => {
      if ("error" in res) { toast.error(res.error); return; }
      const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `hmrc-payouts-${from}-to-${to}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    },
  });

  const applyPreset = (k: "this_month" | "last_month" | "tax_year") => {
    const r = presetRange(k);
    setFrom(r.from); setTo(r.to);
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-2xl px-6 py-10">
        <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Admin</p>
        <h1 className="text-3xl font-light">HMRC payouts export</h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          CSV of bartender payouts within the selected date range for HMRC self-billing reporting.
          Includes contractor identity, shift, role, gross amount, and a VAT column (£0 — not VAT registered) for audit completeness.
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          <button type="button" onClick={() => applyPreset("this_month")} className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">This month</button>
          <button type="button" onClick={() => applyPreset("last_month")} className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">Last month</button>
          <button type="button" onClick={() => applyPreset("tax_year")} className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">UK tax year</button>
        </div>

        <div className="mt-4 grid gap-4 border border-border p-6 sm:grid-cols-2">
          <div>
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Button disabled={mut.isPending} onClick={() => mut.mutate({ data: { from, to } })}>
              {mut.isPending ? "Building…" : "Download CSV"}
            </Button>
          </div>
        </div>

        <section className="mt-10 border border-border p-6">
          <h2 className="text-xl font-light">Annual worker summary (Digital Platform Reporting)</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            One row per worker for a UK tax year (6 Apr – 5 Apr): full legal name, home address,
            date of birth, shifts worked and total earnings — the
            dataset HMRC requires from digital platforms.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <Label htmlFor="tax-year">Tax year</Label>
              <select
                id="tax-year"
                value={taxYear}
                onChange={(e) => setTaxYear(Number(e.target.value))}
                className="mt-1 border border-border bg-background px-3 py-2 text-sm"
              >
                {Array.from({ length: currentTaxYear() - 2019 }, (_, i) => currentTaxYear() - i).map((y) => (
                  <option key={y} value={y}>{taxYearRange(y).label}</option>
                ))}
              </select>
            </div>
            <Button disabled={annual.isPending} onClick={() => annual.mutate({ data: { taxYear, format: "csv" } })}>
              {annual.isPending ? "Building…" : "Download CSV"}
            </Button>
            <Button variant="outline" disabled={annual.isPending} onClick={() => annual.mutate({ data: { taxYear, format: "json" } })}>
              {annual.isPending ? "Building…" : "Download JSON"}
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Every export is written to the admin audit log with the acting admin, tax year and format.
          </p>
        </section>

      </main>
    </div>
  );
}
